import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { DisplayParts } from '@/utils/formatters.ts';

dayjs.extend(utc);
dayjs.extend(timezone);

// Strefa czasowa firmy (IANA), ustawiana raz po zalogowaniu (appRoute.beforeLoad).
// Momenty (stringi ISO z "Z"/offsetem) formatujemy w tej strefie; czas ścienny
// (stringi bez strefy: daty turnusów, terminy rat, daty urodzenia) — bez konwersji.
let companyTimeZone: string | null = null;

export const setCompanyTimeZone = (timeZone: string | null) => {
  companyTimeZone = timeZone;
};

export const getCompanyTimeZone = (): string | null => companyTimeZone;

const INSTANT_SUFFIX_REGEX = /(Z|[+-]\d{2}:\d{2})$/;

const parseForDisplay = (value: string | Date): Dayjs => {
  if (
    typeof value === 'string' &&
    INSTANT_SUFFIX_REGEX.test(value) &&
    companyTimeZone
  ) {
    return dayjs(value).tz(companyTimeZone).locale('pl');
  }
  return dayjs(value).locale('pl');
};

const timeUtils = {
  toDateTimeString: (value: string | Date): string => {
    if (!value) return '-- --';
    return parseForDisplay(value).format('DD MMM YYYY, HH:mm');
  },
  toDateString: (value: string): string => {
    if (!value) return '-- --';
    return parseForDisplay(value).format('DD MMM YYYY');
  },
  toDate: (value: Date | string): string => {
    if (!value) return '-- --';
    return parseForDisplay(value).format('DD MMM YYYY');
  },
  /**
   * Data rozbita na dzień z miesiącem i rok — w tabeli rok idzie wyszarzony.
   * Pokazujemy go zawsze: przy wyjazdach na przełomie brak roku myli.
   */
  splitDate: (value: string | Date): DisplayParts => {
    const date = parseForDisplay(value);
    return { main: date.format('D MMM'), secondary: ` ${date.format('YYYY')}` };
  },
  /** Data z godziną: godzina zawsze za datą i zawsze jako szczegół. */
  splitDateTime: (value: string | Date): DisplayParts => {
    const date = parseForDisplay(value);
    return {
      main: date.format('D MMM YYYY'),
      secondary: ` ${date.format('HH:mm')}`,
    };
  },
  rangeInDaysBetween: (
    startDate: Date | string,
    endDate: Date | string,
    justNumber = true,
  ): string => {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const diff = end.diff(start, 'day') + 1;
    if (justNumber) {
      return diff.toString();
    }
    if (diff === 1) {
      return '1 dzień';
    }
    return `${diff} dni`;
  },
  daysToDisplayString: (daysCount: number): string => {
    if (daysCount === 1) {
      return '1 dzień';
    }
    return `${daysCount} dni`;
  },
  toJavaDate: (date: Date): string => {
    return dayjs(date).format('YYYY-MM-DD');
  },
  toJavaLocalDateTime(date: Date, time?: string | null): string {
    const datePart = dayjs(date).format('YYYY-MM-DD');
    let timePart = time;
    if (!timePart) {
      timePart = '00:00';
    }
    return `${datePart}T${timePart}`;
  },
  getTime: (dateTimeString: string): string => {
    return dayjs(dateTimeString).format('HH:mm');
  },
  toTimeFirstDateTimeString: (value: string | Date): string => {
    return parseForDisplay(value).format('HH:mm, DD MMM YYYY');
  },
  toDateRangeString: (
    start: string | Date | null,
    end: string | Date | null,
  ): string => {
    if (!start || start === '' || !end || end === '') return '';

    const startDate = parseForDisplay(start);
    const endDate = parseForDisplay(end);

    if (!end) {
      return startDate.format('DD MMM YYYY');
    }

    const sameYear = startDate.year() === endDate.year();
    const sameMonth = sameYear && startDate.month() === endDate.month();

    if (sameMonth) {
      // Same month and year: "1-14 lip 2024"
      return `${startDate.format('D')}-${endDate.format('D MMM YYYY')}`;
    }

    if (sameYear) {
      // Same year, different months: "01 lip - 14 sie 2024"
      return `${startDate.format('DD MMM')} - ${endDate.format('DD MMM YYYY')}`;
    }

    // Different years: "01 lip 2024 - 14 sty 2025"
    return `${startDate.format('DD MMM YYYY')} - ${endDate.format('DD MMM YYYY')}`;
  },
  birthDate: (date: string | Date): string => {
    // 03 lut 2015 (10 lat)
    const birthDate = dayjs(date).locale('pl');
    const age = dayjs().diff(birthDate, 'year');
    return `${birthDate.format('DD MMM YYYY')} (${age} lat)`;
  },
  toFullDateRangeWithDuration: (
    start: string | Date,
    end: string | Date,
  ): string => {
    const startDate = dayjs(start).locale('pl');
    const endDate = dayjs(end).locale('pl');
    const days = endDate.diff(startDate, 'day') + 1;
    const daysLabel = days === 1 ? '1 dzień' : `${days} dni`;
    return `${startDate.format('dddd, DD.MM.YYYY')} - ${endDate.format('dddd, DD.MM.YYYY')} (${daysLabel})`;
  },
};

export default timeUtils;
